import React, { useState } from 'react';
import Styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans } from '@lingui/react';
import { Menu, MenuItem } from '@material-ui/core';
import { Box, Container, Flex } from '@components/layout/Grid';
import {
  LiveEventAccess,
  LiveEventKind,
  LiveEventPaymentKind,
} from '@gql/generated';
import { LiveEvent } from '@gql/generated';
import { getFormattedPrice } from './EventListingCard';
import { Button as ButtonV2 } from '@components/ButtonV2';
import InfoIcon from '@assets/InfoOutlineIcon';
import Icon from '@components/Icon';

const DiscountBanner = Styled.div`
  position: sticky;
  top: -1px;
  background-color: #D5E0EC;
  box-shadow: 2px 2px 4px #09080838;
  z-index: 9;
`;

const Text = Styled.p`
  font-size: 14px;
  font-weight: 600;
  margin: 10px 15px 10px 0;
`;

const DiscountBannerText = Styled(Text)`
  flex: 1;
  text-align: center;
  margin-right: 10px;

  a {
    color: #119EDD;
    text-decoration: none;
    margin-left: 4px;
  }

  svg {
    flex-shrink: 0;
    margin-right: 5px;
    margin-top: 3px;
  }
  
  @media (min-width: ${props => props.theme.viewports.mobile}px) {
    text-align: left;
  }
`;

const Button = Styled(ButtonV2)`
  flex-shrink: 0;
`;

const OldPrice = Styled(Text)`
  display: inline-block;
  text-decoration: line-through;
  margin-right: 20px;
`;

type Props = {
  event: LiveEvent;
  userAccess: LiveEventAccess;
  premiumUser: boolean;
};

const EventStickyBanner = ({ event, userAccess, premiumUser }: Props) => {
  const router = useRouter();
  const [anchorElRight, setSnchorElRight] = useState();
  const { lang } = router.query;

  let bannerText, discountMessage;
  let action_btn = (
    <Button
      appearance="dark-blue"
      href={`/${lang}/live/${event.slug}/reserve`}
      disabled={event.isExpired || event.isComingSoon}
    >
      <Trans id="live.event_page.book_btn" />
    </Button>
  );

  if (userAccess) {
    bannerText = (
      <DiscountBannerText>
        <Trans id="live.event_page.join_btn_reserved" />
      </DiscountBannerText>
    );
    action_btn = (
      <Button
        appearance="dark-blue"
        href={userAccess.zoomLink}
        target="_blank"
        rel="noopener"
      >
        <Trans id="live.event_page.join_btn" />
      </Button>
    );
  } else if (event.kind !== LiveEventKind['DonationClass']) {
    if (event.price?.originalPrice === 0) {
      bannerText = (
        <DiscountBannerText>
          <Trans id="live.event_page.event_free" />
        </DiscountBannerText>
      );
    } else {
      bannerText = (
        <>
          {event.price.discountedPrice !== null ? (
            <OldPrice data-testid="event--originalPrice">
              {`${(event.price.originalPrice / 100).toFixed(2)} ${
                event.price.currency
              }`}
            </OldPrice>
          ) : null}
          <DiscountBannerText>{getFormattedPrice(event)}</DiscountBannerText>
        </>
      );
    }
  }

  if (event.kind === LiveEventKind['DonationClass']) {
    discountMessage = <Trans id="live.event_page.banner_donation_class" />;
  } else if (!premiumUser) {
    discountMessage = (
      <Trans
        id="live.event_page.subscription_discount_text"
        components={[<a key="0" href="#subscription-offers"></a>]}
      />
    );
  } else if (event.price.discountedPrice === 0) {
    discountMessage = <Trans id="live.event_page.sticky_banner.free_for_you" />;
  } else {
    const discount = event?.price?.discountApplied
      ? 100 -
        (event?.price?.discountedPrice / event?.price?.originalPrice) * 100
      : 30;
    discountMessage = (
      <Trans
        id="live.event_page.sticky_banner.discount_available"
        values={{ discount: Math.floor(discount) }}
      />
    );
  }

  const onOpenMenu = e => {
    //
    setSnchorElRight(e.target);
  };

  const onCloseMenu = () => {
    //
    setSnchorElRight(null);
  };

  return (
    <DiscountBanner data-testid="EventStickyBanner">
      <Container
        flexWrap="wrap"
        flexDirection={['column', 'column', 'row', 'row']}
        justifyContent={'flex-end'}
        alignItems="center"
        py={[10]}
        px={[10, 20, 20]}
      >
        {!userAccess && (
          <DiscountBannerText>
            <InfoIcon />
            {discountMessage}
          </DiscountBannerText>
        )}
        <Flex flex={1} justifyContent="flex-end">
          <Flex alignItems="center">
            <Flex>{bannerText}</Flex>
            {action_btn}
          </Flex>
          {userAccess &&
          event.paymentKind !== LiveEventPaymentKind['FreeForAll'] ? (
            <Box p={20}>
              <Button appearance="dark" link onClick={onOpenMenu}>
                <Icon name="ygb-icon-menu-verb" fontSize="20px" />
              </Button>
              <Menu
                anchorEl={anchorElRight}
                open={Boolean(anchorElRight)}
                onClose={onCloseMenu}
                transformOrigin={{ vertical: -50, horizontal: 10 }}
              >
                <MenuItem>
                  <Link
                    className="color-inherit"
                    href={`/${lang}/live/${event.slug}/buy-gift`}
                  >
                    <Trans id="live.payment_page.tabs.buy_gift" />
                  </Link>
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Flex>
      </Container>
    </DiscountBanner>
  );
};

export default EventStickyBanner;
